import { yup } from '@framework/validation';
import { stripHtml } from 'string-strip-html';
import { IBooleanCondition } from '../../../../inputs';
import { BooleanConditionSchema } from '../../../../inputs/expressions-editor/conditions/BooleanConditionSchema';
import {
  IConditionData,
  WidgetConditionManager,
} from '../../../../validator/WidgetConditionManager';
import { evaluateExpression } from '../../../expressions/evaluate/evaluateExpression';
import { createEvaluationVariables } from '../../../expressions/evaluate/variables/IEvaluationVariables';
import { RuleType } from '../../IRule';
import { IRuleType } from '../../IRuleType';
import { CustomWarningsRuleComponent } from './CustomWarningsRuleComponent';
import { CustomWarningRuleType } from './ICustomWarningRule';

const CustomWarningsRuleSchema: yup.ObjectSchema<
  ICustomWarningRuleType | null | undefined
> = yup
  .object({
    rules: yup
      .array()
      .of(
        yup
          .object({
            type: yup
              .string()
              .oneOfEnum(CustomWarningRuleType)
              .required()
              .label('Livello'),
            message: yup.string().required().label('Messaggio'),
            condition: BooleanConditionSchema, // TODO -> al momento ritorna un Lazy che non ha la funzione ".label" // .label('Condizione')
          })
          .required(),
      )
      .notRequired()
      .nullable(),
  })
  .notRequired()
  .noUnknown()
  .nullable();

export type ICustomWarningRuleType =
  | {
      rules?: ICustomWarning[] | null;
    }
  | null
  | undefined;

export interface ICustomWarning {
  type: CustomWarningRuleType;
  message: string;
  condition: IBooleanCondition | null;
}

export const CustomWarningsRule: IRuleType<ICustomWarningRuleType> = {
  type: RuleType.CustomWarnings,
  name: 'Accertamenti personalizzati',
  configComponent: CustomWarningsRuleComponent,
  configValidator: CustomWarningsRuleSchema,
  validator: (schema, rule) => {
    if (!rule?.rules) return schema;

    rule.rules.forEach(rule => {
      const conditionData = WidgetConditionManager.getConditionData(
        rule.condition,
      );

      if (!conditionData) return schema;

      if (rule.type === CustomWarningRuleType.Error) {
        schema = getErrorTestFromCondition(schema, rule, conditionData);
      }
      if (rule.type === CustomWarningRuleType.Warning) {
        schema = getWarningTestFromCondition(schema, rule, conditionData);
      }
    });
    return schema;
  },
};

/**
 * Ritorna lo schema con la condizione per il test sull'errore custom.
 */
function getErrorTestFromCondition(
  schema: yup.AnySchema,
  rule: ICustomWarning,
  conditionData: IConditionData,
) {
  if (!conditionData) return schema;
  if (conditionData.condition.type === 'static') {
    return conditionData.condition.value
      ? schema.test({
          name: 'custom-warning',
          message: stripHtml(rule.message).result.trim(),
          test: () => false,
        })
      : schema;
  }

  const { paths, condition, references } = conditionData;

  return schema.parentWhen(paths, (values, schema: any, context: any) => {
    const variables = createEvaluationVariables(references, values);

    const throwError = evaluateExpression(condition.expression, { variables });

    return schema
      .test({
        name: 'custom-warning',
        message: stripHtml(rule.message).result.trim(),
        test: (value: any, context: any) => {
          if (context.schema?.spec?.strip) return true;
          return !throwError;
        },
      })
      .meta({
        customErrorDeps: [...paths, ...(schema.meta().customErrorDeps ?? [])],
      });
  });
}

/**
 * Ritorna lo schema con la gestione del warning custom.
 * Il warning è un campo meta impostato sullo schema, utilizzando
 * un metodo custom di yup per centralizzare la logica.
 */
function getWarningTestFromCondition(
  schema: yup.AnySchema,
  rule: ICustomWarning,
  conditionData: IConditionData,
) {
  if (!conditionData) return schema;
  if (conditionData.condition.type === 'static') {
    return conditionData.condition.value
      ? schema.warningMeta(rule.message)
      : schema;
  }

  const { paths, condition, references } = conditionData;

  return schema.parentWhen(paths, (values, schema: any) => {
    const variables = createEvaluationVariables(references, values);

    const addWarning = evaluateExpression(condition.expression, { variables });

    return addWarning
      ? schema.warningMeta(stripHtml(rule.message).result.trim())
      : schema;
  });
}
